import { FC, memo, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { GLOBALS } from '../globals'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'

import { createBoxVariantComponents, createMostlyFluidVariantComponents } from '../setup-factories'
import { TextTitleSmall } from '../typography/titles'
import { BoxMarginBottom10px, BoxMarginBottom20px } from '../structural/box-margins'
import { InputText } from '../layout/inputs'
import { ButtonSmall } from './button'
import { TextStandard } from '../typography/texts'

const { MostlyFluidContainer } = createMostlyFluidVariantComponents({
  Container: {
    horizontalPadding: '4px',
    verticalPadding: '15px',
    widths: ['25%'],
    alignItems: 'flex-end',
    breakpoints: { medium: { widths: ['33.333%'] }, small: { widths: ['50%'] } }
  }
})
const StyledMostlyFluidContainer = styled(MostlyFluidContainer)`
  padding-bottom: 40px;
  flex: 1 1 auto;
  overflow: auto;
  align-content: flex-start;
  margin-top: -20px;
`
const { BoxStretchContainer } = createBoxVariantComponents({
  StretchContainer: {
    flexGrow: 1
  }
})
const { MostlyFluidInputWithButton } = createMostlyFluidVariantComponents({
  InputWithButton: {
    horizontalPadding: '10px',
    widths: ['100%', 'auto']
  }
})
const StyledMostlyFluidInputWithButton = styled(MostlyFluidInputWithButton)`
  flex-wrap: nowrap;
`
const { BoxItem } = createBoxVariantComponents({
  Item: {
    tagName: 'button',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    margin: { top: '4px' },
    display: 'block',
    height: '60px',
    border: { radius: '5px' }
  }
})
const StyledBoxItem = styled(BoxItem)<{ $color: string }>`
  border: 2px solid white;
  cursor: pointer;
  opacity: 1;
  transition: opacity 0.2s;
  background-color: ${(p) => p.$color};
  &:hover {
    opacity: 0.8;
  }
`
const StyledBoxItemActive = styled(StyledBoxItem)`
  pointer-events: none;
  box-shadow: 0 0 0 2px ${GLOBALS.color.turquoise[500]};
  > * {
    font-size: 16px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

interface ColorItemProps {
  colorOption: Window['colorConfiguratorColorOptions'][number]
  $active: boolean
  onSelect: (colorTitle: Window['colorConfiguratorColorOptions'][number]) => void
}
const ColorItem: FC<ColorItemProps> = memo(({ colorOption, $active, onSelect }) => {
  const onColorClick = useCallback(() => onSelect(colorOption), [colorOption, onSelect])

  return (
    <>
      <TextTitleSmall>{colorOption.title}</TextTitleSmall>
      {$active === true ? (
        <StyledBoxItemActive $color={colorOption.hexCode}>
          <FontAwesomeIcon icon={faCheckCircle} />
        </StyledBoxItemActive>
      ) : (
        <StyledBoxItem $color={colorOption.hexCode} onClick={onColorClick} />
      )}
    </>
  )
})

interface ColorChoiceProps {
  colorOptions: Window['colorConfiguratorColorOptions']
  selectedColorOption: Window['colorConfiguratorColorOptions'][number] | undefined
  setColorOption: (colorTitle: Window['colorConfiguratorColorOptions'][number]) => void
}

export const ColorChoice: FC<ColorChoiceProps> = memo(({ colorOptions, selectedColorOption, setColorOption }) => {
  const [colorSearch, setColorSearch] = useState('')
  const [colorOptionFreeText, setColorOptionFreeText] = useState('')
  const onColorChanged = useCallback((value: string) => {
    setColorSearch(value)
  }, [])

  const displayedColorOptions = useMemo(() => {
    if (colorSearch == null || colorSearch === '') {
      return colorOptions
    }
    return colorOptions.filter((co) => co.title?.toLowerCase().includes(colorSearch.toLowerCase()))
  }, [colorOptions, colorSearch])

  const onSelectColorOptionFreeText = useCallback(() => {
    window.colorConfiguratorSelectColorTitle(colorOptionFreeText ?? '')
  }, [colorOptionFreeText])

  return (
    <>
      <BoxMarginBottom20px>
        <InputText value={colorSearch} onChange={onColorChanged} placeholder="Indtast farvekode eller navn" />
      </BoxMarginBottom20px>
      {displayedColorOptions.length === 0 ? (
        <>
          <BoxMarginBottom10px>
            <TextStandard>
              Fandt du ikke den farve du søgte? Du kan skrive dine ønsker til farve i tekstfeltet her:
            </TextStandard>
          </BoxMarginBottom10px>

          <StyledMostlyFluidInputWithButton>
            <InputText value={colorOptionFreeText} onChange={setColorOptionFreeText} />
            <ButtonSmall type="button" onClick={onSelectColorOptionFreeText}>
              Gem farveønske
            </ButtonSmall>
          </StyledMostlyFluidInputWithButton>
          <BoxStretchContainer />
        </>
      ) : (
        <StyledMostlyFluidContainer>
          {displayedColorOptions.map((colorOption, index) => {
            return (
              <ColorItem
                colorOption={colorOption}
                $active={colorOption === selectedColorOption}
                onSelect={setColorOption}
                key={index}
              />
            )
          })}
        </StyledMostlyFluidContainer>
      )}
    </>
  )
})
